import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Play, Pause, Plus, Trash2, MousePointer2, Repeat, X, Save, Eraser, ArrowRight } from "lucide-react";
import { buildRouteCache, positionAt } from "@/lib/playAnimation";

// Hook: runs requestAnimationFrame and returns interpolated positions for a play
export function usePlayAnimation({ play, playing, loop, onComplete }) {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const startTsRef = useRef(0);
  const cache = useMemo(() => buildRouteCache(play), [play]);

  useEffect(() => {
    if (!playing || !play) {
      cancelAnimationFrame(rafRef.current);
      return;
    }
    startTsRef.current = performance.now();
    const duration = play.duration_ms || 3000;

    const tick = (now) => {
      const elapsed = now - startTsRef.current;
      const p = elapsed / duration;
      if (p >= 1) {
        if (loop) {
          startTsRef.current = now;
          setProgress(0);
          rafRef.current = requestAnimationFrame(tick);
        } else {
          setProgress(1);
          if (onComplete) onComplete();
        }
        return;
      }
      setProgress(p);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [play, playing, loop, onComplete]);

  // Compute positions map
  const positions = useMemo(() => {
    if (!play) return null;
    const out = {};
    for (const slotId of Object.keys(cache)) {
      const { segments } = cache[slotId];
      out[slotId] = positionAt(segments, progress);
    }
    return out;
  }, [cache, play, progress]);

  return { positions, progress, routesCache: cache };
}

// SVG overlay drawing the routes (with arrow at end of last waypoint).
// Coordinates are in 0-100 percentage space.
export function RoutesOverlay({ routes, opacity = 0.85, color = "#FACC15" }) {
  if (!routes || routes.length === 0) return null;
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none z-20"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      data-testid="routes-overlay"
    >
      <defs>
        <marker id="play-arrow" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
        </marker>
      </defs>
      {routes.map((r, idx) => {
        const pts = [r.start, ...(r.waypoints || [])];
        if (pts.length < 2) return null;
        const d = pts
          .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
          .join(" ");
        return (
          <path
            key={r.slot_id || idx}
            d={d}
            fill="none"
            stroke={color}
            strokeWidth="0.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="1.5 1"
            opacity={opacity}
            markerEnd="url(#play-arrow)"
          />
        );
      })}
    </svg>
  );
}

// Bar of saved plays with controls: ▶ play, design new, delete, loop.
export function PlaysBar({
  category,
  unit,
  formatType = "11v11",
  canEdit,
  activePlayId,
  onPlay,
  onStopPlay,
  onStartDesign,
  loop,
  setLoop,
  designing,
}) {
  const [plays, setPlays] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/plays?category=${category}&unit=${unit}&format_type=${formatType}`);
      setPlays(r.data || []);
    } catch {
      setPlays([]);
    } finally { setLoading(false); }
  }, [category, unit, formatType]);

  useEffect(() => { load(); }, [load]);

  // Listen for save events from designer
  useEffect(() => {
    const fn = () => load();
    window.addEventListener("plays-reload", fn);
    return () => window.removeEventListener("plays-reload", fn);
  }, [load]);

  const remove = async (id, name) => {
    if (!confirm(`¿Eliminar la play "${name}"?`)) return;
    try {
      await api.delete(`/plays/${id}`);
      toast.success("Play eliminada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="tactical-card p-3 space-y-2" data-testid="plays-bar">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Play size={12} className="text-[#FACC15]"/>
          <span className="overline">Plays animadas</span>
          {loading && <span className="text-[10px] text-[#71717A]">cargando...</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setLoop(!loop)}
            className={`text-[10px] uppercase tracking-wider px-2 py-1 border flex items-center gap-1 ${loop ? "border-[#FACC15] text-[#FACC15] bg-[#1A1A1A]" : "border-[#27272A] text-[#A1A1AA]"}`}
            data-testid="play-loop-toggle"
            title="Reproducir en bucle"
          >
            <Repeat size={10}/> Loop
          </button>
          {canEdit && !designing && (
            <button
              onClick={onStartDesign}
              className="btn-primary text-[10px] px-2 py-1 flex items-center gap-1"
              data-testid="design-play-btn"
              title="Crear nueva play"
            >
              <Plus size={10}/> Crear play
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-[#27272A]">
        {plays.length === 0 ? (
          <span className="text-[10px] text-[#71717A]">Sin plays guardadas{canEdit ? " · diseña la primera" : ""}.</span>
        ) : (
          plays.map(p => {
            const isActive = activePlayId === p.id;
            return (
              <div key={p.id} className={`flex items-center border ${isActive ? "border-[#FACC15]" : "border-[#27272A] hover:border-[#FACC15]/60"}`}>
                <button
                  onClick={() => isActive ? onStopPlay() : onPlay(p)}
                  className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold ${isActive ? "text-[#FACC15] bg-[#1A1A1A]" : "text-white hover:bg-[#1A1A1A]"} flex items-center gap-1`}
                  data-testid={`play-btn-${p.name}`}
                  title={isActive ? "Detener" : "Reproducir"}
                >
                  {isActive ? <Pause size={10}/> : <Play size={10}/>}
                  {p.name}
                </button>
                {canEdit && (
                  <button onClick={() => remove(p.id, p.name)} className="px-1.5 py-1 text-[#71717A] hover:text-[#FF3B30] border-l border-[#27272A]" title="Eliminar" data-testid={`del-play-${p.name}`}>
                    <Trash2 size={9}/>
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// Designer toolbar + slot selector. Rendered as a fixed bottom bar over the field.
export function PlayDesignerToolbar({
  slots,
  playerMap,
  routes,             // {slotId: [{x,y}]}
  selectedSlotId,
  setSelectedSlotId,
  onClearRoute,
  onCancel,
  onSave,
  category,
  unit,
}) {
  const starters = useMemo(() => slots.filter(s => s.slot === "starter"), [slots]);

  return (
    <div className="tactical-card p-4 mb-3 space-y-3" style={{ borderColor: "#FACC15", background: "linear-gradient(120deg, rgba(250,204,21,0.08) 0%, transparent 60%)" }} data-testid="play-designer-toolbar">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MousePointer2 size={14} className="text-[#FACC15]"/>
          <div className="overline text-[#FACC15]">Diseñando play · {category}/{unit}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="btn-ghost text-xs flex items-center gap-1" data-testid="designer-cancel">
            <X size={12}/> Cancelar
          </button>
          <button onClick={onSave} className="btn-primary text-xs flex items-center gap-1" data-testid="designer-save">
            <Save size={12}/> Guardar play
          </button>
        </div>
      </div>
      <div className="text-[10px] text-[#A1A1AA] leading-relaxed">
        <span className="font-bold text-white">1.</span> Selecciona un jugador abajo. <span className="font-bold text-white">2.</span> Haz click en el campo para añadir waypoints (intermedios + destino final). <span className="font-bold text-white">3.</span> El último click es la posición post-snap.
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pt-2 border-t border-[#27272A]">
        {starters.map(s => {
          const player = s.player_id ? playerMap[s.player_id] : null;
          const count = (routes[s.id] || []).length;
          const isSel = selectedSlotId === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setSelectedSlotId(s.id)}
              className={`px-2 py-1 text-[10px] uppercase tracking-wider font-bold border flex items-center gap-1.5 transition-colors ${
                isSel ? "border-[#FACC15] bg-[#1A1A1A] text-[#FACC15]" : count > 0 ? "border-[#34C759] text-[#34C759]" : "border-[#27272A] text-[#A1A1AA] hover:text-white"
              }`}
              data-testid={`designer-slot-${s.position}`}
            >
              <span>{s.position}</span>
              {player && <span className="text-[9px] opacity-70 normal-case">{player.profile?.jersey_number ?? "·"}</span>}
              {count > 0 && (
                <span className="ml-1 px-1 bg-black/40 text-[9px]"><ArrowRight size={8} className="inline"/> {count}</span>
              )}
              {isSel && count > 0 && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onClearRoute(s.id); }}
                  className="ml-1 text-[#FF3B30] hover:text-white"
                  title="Limpiar ruta"
                  data-testid={`designer-clear-${s.position}`}
                >
                  <Eraser size={10}/>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
